import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  linkedSignal,
  output,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  HandleSideBarService,
  NavItem,
  SideBarFacadeService,
} from '@data-access/shared';
import { MatRippleModule } from '@angular/material/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatRippleModule],
  selector: 'wcg-side-nav-tabs',
  styleUrl: './nav.component.scss',
  template: `
    <nav class="mat-tab-nav">
      @for (item of navItems().values(); track item.path) {
        @if (item.path) {
          <a
            href="#"
            (click)="$event.preventDefault(); onTabClick(item)"
            [class.mat-tab-active]="selectedTab() === item.path"
            class="mat-tab-link"
            role="button"
            matRipple
          >
            {{ item.label }}
            @if (selectedTab() === item.path) {
              <span class="mat-tab-indicator"></span>
            }
          </a>
        }
      }
    </nav>
  `,
})
export class SideNavTabsComponent {
  navItems = input<Set<NavItem>>(new Set<NavItem>([]));
  selectedTab = input<string>('');
  tabSelected = output<NavItem>();

  onTabClick(item: NavItem) {
    this.tabSelected.emit(item);
  }
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatSidenavModule,
    NgTemplateOutlet,
    MatCardModule,
    MatIconModule,
    NgTemplateOutlet,
    NgClass,
    SideNavTabsComponent,
    MatTooltipModule,
  ],
  selector: 'wcg-side-bar',
  styles: `
    @use '../../../../shared/styles/index.scss';
    .side-bar-container {
      display: flex;
      transition: width 0.3s ease;
      overflow: hidden !important;
      height: 30rem;

      .mat-drawer {
        transition:
          width 0.3s ease,
          opacity 0.3s ease;
        width: 100%;
        opacity: 1;
      }

      .mat-drawer.collapsed {
        width: 0;
        opacity: 0;
      }

      .mat-drawer-content {
        flex: 1;
        transition: margin 0.3s ease;
        background-color: #fff;
      }
    }

    /*
 @NOTE: Collapse, expand classes
*/
    .collapsed {
      width: 6rem;
    }
    .side-bar {
      &__title-border {
        @extend %border-bottom;
      }
    }
  `,
  template: `
    <mat-drawer-container
      class="side-bar-container rounded-lg"
      [style.height]="height()"
      [ngClass]="{ collapsed: !drawer.opened }"
    >
      <mat-drawer
        #drawer
        class="example-sidenav"
        mode="side"
        position="end"
        [ngClass]="{ collapsed: !drawer.opened }"
        [opened]="$toggleSideBar()"
      >
        <div class="w-full flex flex-col h-full">
          <div
            class="w-full flex justify-between items-center px-4 side-bar__title-border"
          >
            @if (sidebarHeader()) {
              <div class="flex items-center gap-2 py-4">
                <h4>{{ sidebarHeader() }}</h4>
                @if (headerTooltip()) {
                  <mat-icon
                    fontSet="material-symbols-outlined"
                    class="text-2xl"
                    [matTooltip]="headerTooltip()"
                    matTooltipPosition="right"
                  >
                    info
                  </mat-icon>
                }
              </div>
            }
            @if (navItems().size > 0) {
              <wcg-side-nav-tabs
                [navItems]="navItems()"
                [selectedTab]="selectedTab()"
                (tabSelected)="onTabSelected($event)"
              />
            }
            <div class="flex gap-4">
              @defer (when exportButtonTemplate()) {
                <ng-container [ngTemplateOutlet]="exportButtonTemplate()!" />
              }
              <mat-icon
                fontSet="material-symbols-outlined"
                class="cursor-pointer"
                data-testid="close-sidebar-button"
                (click)="updateToggleSideBar(false)"
                >{{ closeIcon() }}</mat-icon
              >
            </div>
          </div>
          <div class="w-full py-6 px-4">
            <ng-template #sideBar />
          </div>
        </div>
      </mat-drawer>
      <mat-drawer-content>
        <div class="p-6">
          <button
            type="button"
            mat-flat-button
            (click)="updateToggleSideBar(true)"
            data-testid="open-sidebar-button"
          >
            <mat-icon fontSet="material-symbols-outlined">{{
              openIcon()
            }}</mat-icon>
          </button>
        </div>
      </mat-drawer-content>
    </mat-drawer-container>
  `,
})
export class WcgSideBarComponent {
  protected readonly open = open;
  protected readonly parseInt = parseInt;
  private readonly _sideBarFacadeService = inject(SideBarFacadeService);
  private readonly _handleSideBarService = inject(HandleSideBarService);
  sidebarHeader = input<string>('');
  headerTooltip = input<string>('');
  height = input<string>('68vh');
  closeIcon = input<string>('chevron_right');
  openIcon = input<string>('chevron_left');
  navItems = input<Set<NavItem>>(new Set<NavItem>());
  selectedTab = input<string>('');

  sidebarOpened = output<boolean>();
  tabSelected = output<NavItem>();

  $toggleSideBar = linkedSignal({
    computation: (toggleSideBar) => {
      const _toggleSideBar = toggleSideBar;
      this.sidebarOpened.emit(toggleSideBar);
      return _toggleSideBar;
    },
    source: this._sideBarFacadeService.$toggleSideBar,
  });

  sideBarContainerRef = viewChild('sideBar', { read: ViewContainerRef });

  exportButtonTemplate = input<TemplateRef<unknown>>();

  updateToggleSideBar(toggleSideBar: boolean) {
    this._handleSideBarService.setToggleSideBarOpen(toggleSideBar);
  }

  onTabSelected(item: NavItem) {
    this.tabSelected.emit(item);
  }
}
